import {
  Widget,
  isWidget
} from "./chunk-ZFACQRIZ.js";
import {
  View
} from "./chunk-R4FN2FPU.js";
import {
  DataTransfer,
  DomEmitterMixin,
  DomEventObserver,
  EventInfo,
  LiveRange,
  MouseObserver,
  Plugin,
  Rect,
  createElement,
  delay,
  env_default,
  global_default,
  resizeobserver_default,
  throttle_default,
  toUnit,
  uid
} from "./chunk-H3OD3CNY.js";

// node_modules/@ckeditor/ckeditor5-clipboard/src/clipboardobserver.js
var ClipboardObserver = class extends DomEventObserver {
  constructor(view) {
    super(view);
    this.domEventType = [
      "paste",
      "copy",
      "cut",
      "drop",
      "dragover",
      "dragstart",
      "dragend",
      "dragenter",
      "dragleave"
    ];
    const viewDocument = this.document;
    this.listenTo(viewDocument, "paste", handleInput("clipboardInput"), { priority: "low" });
    this.listenTo(viewDocument, "drop", handleInput("clipboardInput"), { priority: "low" });
    this.listenTo(viewDocument, "dragover", handleInput("dragging"), { priority: "low" });
    function handleInput(type) {
      return (evt, data) => {
        data.preventDefault();
        const targetRanges = data.dropRange ? [data.dropRange] : null;
        const eventInfo = new EventInfo(viewDocument, type);
        viewDocument.fire(eventInfo, {
          dataTransfer: data.dataTransfer,
          method: evt.name,
          targetRanges,
          target: data.target,
          domEvent: data.domEvent
        });
        if (eventInfo.stop.called) {
          data.stopPropagation();
        }
      };
    }
  }
  onDomEvent(domEvent) {
    const nativeDataTransfer = "clipboardData" in domEvent ? domEvent.clipboardData : domEvent.dataTransfer;
    const cacheFiles = domEvent.type == "drop" || domEvent.type == "paste";
    const evtData = {
      dataTransfer: new DataTransfer(nativeDataTransfer, { cacheFiles })
    };
    if (domEvent.type == "drop" || domEvent.type == "dragover") {
      evtData.dropRange = getDropViewRange(this.view, domEvent);
    }
    this.fire(domEvent.type, domEvent, evtData);
  }
};
function getDropViewRange(view, domEvent) {
  const domDoc = domEvent.target.ownerDocument;
  const x = domEvent.clientX;
  const y = domEvent.clientY;
  let domRange;
  if (domDoc.caretRangeFromPoint && domDoc.caretRangeFromPoint(x, y)) {
    domRange = domDoc.caretRangeFromPoint(x, y);
  } else if (domEvent.rangeParent) {
    domRange = domDoc.createRange();
    domRange.setStart(domEvent.rangeParent, domEvent.rangeOffset);
    domRange.collapse(true);
  }
  if (domRange) {
    return view.domConverter.domRangeToView(domRange);
  }
  return null;
}

// node_modules/@ckeditor/ckeditor5-clipboard/src/utils/plaintexttohtml.js
function plainTextToHtml(text) {
  text = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\r?\n\r?\n/g, "</p><p>").replace(/\r?\n/g, "<br>").replace(/\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;").replace(/^\s/, "&nbsp;").replace(/\s$/, "&nbsp;").replace(/\s\s/g, " &nbsp;");
  if (text.includes("</p><p>") || text.includes("<br>")) {
    text = `<p>${text}</p>`;
  }
  return text;
}

// node_modules/@ckeditor/ckeditor5-clipboard/src/utils/normalizeclipboarddata.js
function normalizeClipboardData(data) {
  return data.replace(/<span(?: class="Apple-converted-space"|)>(\s+)<\/span>/g, (fullMatch, spaces) => {
    if (spaces.length == 1) {
      return " ";
    }
    return spaces;
  }).replace(/<!--[\s\S]*?-->/g, "");
}

// node_modules/@ckeditor/ckeditor5-clipboard/src/utils/viewtoplaintext.js
var smallPaddingElements = ["figcaption", "li"];
var listElements = ["ol", "ul"];
function viewToPlainText(viewItem) {
  if (viewItem.is("$text") || viewItem.is("$textProxy")) {
    return viewItem.data;
  }
  if (viewItem.is("element", "img") && viewItem.hasAttribute("alt")) {
    return viewItem.getAttribute("alt");
  }
  if (viewItem.is("element", "br")) {
    return "\n";
  }
  let text = "";
  let prev = null;
  for (const child of viewItem.getChildren()) {
    text += newLinePadding(child, prev) + viewToPlainText(child);
    prev = child;
  }
  return text;
}
function newLinePadding(element, previous) {
  if (!previous) {
    return "";
  }
  if (element.is("element", "li") && !element.isEmpty && element.getChild(0).is("containerElement")) {
    return "\n\n";
  }
  if (listElements.includes(element.name) && listElements.includes(previous.name)) {
    return "\n\n";
  }
  if (!element.is("containerElement") && !previous.is("containerElement")) {
    return "";
  }
  if (smallPaddingElements.includes(element.name) || smallPaddingElements.includes(previous.name)) {
    return "\n";
  }
  return "\n\n";
}

// node_modules/@ckeditor/ckeditor5-clipboard/src/clipboardpipeline.js
var ClipboardPipeline = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ClipboardPipeline";
  }
  /**
   * @inheritDoc
   */
  init() {
    const editor = this.editor;
    const view = editor.editing.view;
    view.addObserver(ClipboardObserver);
    this._setupPasteDrop();
    this._setupCopyCut();
  }
  /**
   * Fires Clipboard `'outputTransformation'` event for given parameters.
   *
   * @internal
   */
  _fireOutputTransformationEvent(dataTransfer, selection, method) {
    const content = this.editor.model.getSelectedContent(selection);
    this.fire("outputTransformation", {
      dataTransfer,
      content,
      method
    });
  }
  /**
   * The clipboard paste pipeline.
   */
  _setupPasteDrop() {
    const editor = this.editor;
    const model = editor.model;
    const view = editor.editing.view;
    const viewDocument = view.document;
    this.listenTo(viewDocument, "clipboardInput", (evt, data) => {
      if (data.method == "paste" && !editor.model.canEditAt(editor.model.document.selection)) {
        evt.stop();
      }
    }, { priority: "highest" });
    this.listenTo(viewDocument, "clipboardInput", (evt, data) => {
      const dataTransfer = data.dataTransfer;
      let content;
      if (data.content) {
        content = data.content;
      } else {
        let contentData = "";
        if (dataTransfer.getData("text/html")) {
          contentData = normalizeClipboardData(dataTransfer.getData("text/html"));
        } else if (dataTransfer.getData("text/plain")) {
          contentData = plainTextToHtml(dataTransfer.getData("text/plain"));
        }
        content = this.editor.data.htmlProcessor.toView(contentData);
      }
      const eventInfo = new EventInfo(this, "inputTransformation");
      this.fire(eventInfo, {
        content,
        dataTransfer,
        targetRanges: data.targetRanges,
        method: data.method
      });
      if (eventInfo.stop.called) {
        evt.stop();
      }
      view.scrollToTheSelection();
    }, { priority: "low" });
    this.listenTo(this, "inputTransformation", (evt, data) => {
      if (data.content.isEmpty) {
        return;
      }
      const dataController = this.editor.data;
      const modelFragment = dataController.toModel(data.content, "$clipboardHolder");
      if (modelFragment.childCount == 0) {
        return;
      }
      evt.stop();
      model.change(() => {
        this.fire("contentInsertion", {
          content: modelFragment,
          method: data.method,
          dataTransfer: data.dataTransfer,
          targetRanges: data.targetRanges
        });
      });
    }, { priority: "low" });
    this.listenTo(this, "contentInsertion", (evt, data) => {
      data.resultRange = model.insertContent(data.content);
    }, { priority: "low" });
  }
  /**
   * The clipboard copy/cut pipeline.
   */
  _setupCopyCut() {
    const editor = this.editor;
    const modelDocument = editor.model.document;
    const view = editor.editing.view;
    const viewDocument = view.document;
    const onCopyCut = (evt, data) => {
      const dataTransfer = data.dataTransfer;
      data.preventDefault();
      this._fireOutputTransformationEvent(dataTransfer, modelDocument.selection, evt.name);
    };
    this.listenTo(viewDocument, "copy", onCopyCut, { priority: "low" });
    this.listenTo(viewDocument, "cut", (evt, data) => {
      if (!editor.model.canEditAt(editor.model.document.selection)) {
        data.preventDefault();
      } else {
        onCopyCut(evt, data);
      }
    }, { priority: "low" });
    this.listenTo(this, "outputTransformation", (evt, data) => {
      const content = editor.data.toView(data.content);
      viewDocument.fire("clipboardOutput", {
        dataTransfer: data.dataTransfer,
        content,
        method: data.method
      });
    }, { priority: "low" });
    this.listenTo(viewDocument, "clipboardOutput", (evt, data) => {
      if (!data.content.isEmpty) {
        data.dataTransfer.setData("text/html", this.editor.data.htmlProcessor.toData(data.content));
        data.dataTransfer.setData("text/plain", viewToPlainText(data.content));
      }
      if (data.method == "cut") {
        editor.model.deleteContent(modelDocument.selection);
      }
    }, { priority: "low" });
  }
};

// node_modules/@ckeditor/ckeditor5-clipboard/src/lineview.js
var toPx = toUnit("px");
var LineView = class extends View {
  /**
   * @inheritDoc
   */
  constructor() {
    super();
    const bind = this.bindTemplate;
    this.set({
      isVisible: false,
      left: null,
      top: null,
      width: null
    });
    this.setTemplate({
      tag: "div",
      attributes: {
        class: [
          "ck",
          "ck-clipboard-drop-target-line",
          bind.if("isVisible", "ck-hidden", (value) => !value)
        ],
        style: {
          left: bind.to("left", (left) => toPx(left)),
          top: bind.to("top", (top) => toPx(top)),
          width: bind.to("width", (width) => toPx(width))
        }
      }
    });
  }
};

// node_modules/@ckeditor/ckeditor5-clipboard/src/dragdroptarget.js
var DragDropTarget = class extends Plugin {
  constructor() {
    super(...arguments);
    this.removeDropMarkerDelayed = delay(() => this.removeDropMarker(), 40);
    this._updateDropMarkerThrottled = throttle_default((targetRange) => this._updateDropMarker(targetRange), 40);
    this._reconvertMarkerThrottled = throttle_default(() => {
      if (this.editor.model.markers.has("drop-target")) {
        this.editor.editing.reconvertMarker("drop-target");
      }
    }, 0);
    this._dropTargetLineView = new LineView();
    this._domEmitter = new (DomEmitterMixin())();
    this._scrollables = /* @__PURE__ */ new Map();
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "DragDropTarget";
  }
  /**
   * @inheritDoc
   */
  init() {
    this._setupDropMarker();
  }
  /**
   * @inheritDoc
   */
  destroy() {
    this._domEmitter.stopListening();
    for (const { resizeObserver } of this._scrollables.values()) {
      resizeObserver.destroy();
    }
    this._updateDropMarkerThrottled.cancel();
    this.removeDropMarkerDelayed.cancel();
    this._reconvertMarkerThrottled.cancel();
    return super.destroy();
  }
  /**
   * Finds the drop target range and updates the drop marker.
   *
   * @internal
   */
  updateDropMarker(targetViewElement, targetViewRanges, clientX, clientY, blockMode, draggedRange) {
    this.removeDropMarkerDelayed.cancel();
    const targetRange = findDropTargetRange(this.editor, targetViewElement, targetViewRanges, clientX, clientY, blockMode, draggedRange);
    if (!targetRange) {
      return;
    }
    if (draggedRange && draggedRange.containsRange(targetRange)) {
      return this.removeDropMarker();
    }
    this._updateDropMarkerThrottled(targetRange);
  }
  /**
   * Finds the final drop target range.
   *
   * @internal
   */
  getFinalDropRange(targetViewElement, targetViewRanges, clientX, clientY, blockMode, draggedRange) {
    const targetRange = findDropTargetRange(this.editor, targetViewElement, targetViewRanges, clientX, clientY, blockMode, draggedRange);
    this.removeDropMarker();
    return targetRange;
  }
  /**
   * Removes the drop target marker.
   *
   * @internal
   */
  removeDropMarker() {
    const model = this.editor.model;
    this.removeDropMarkerDelayed.cancel();
    this._updateDropMarkerThrottled.cancel();
    this._dropTargetLineView.isVisible = false;
    if (model.markers.has("drop-target")) {
      model.change((writer) => {
        writer.removeMarker("drop-target");
      });
    }
  }
  /**
   * Creates downcast conversion for the drop target marker.
   */
  _setupDropMarker() {
    const editor = this.editor;
    editor.ui.view.body.add(this._dropTargetLineView);
    editor.conversion.for("editingDowncast").markerToHighlight({
      model: "drop-target",
      view: {
        classes: ["ck-clipboard-drop-target-range"]
      }
    });
    editor.conversion.for("editingDowncast").markerToElement({
      model: "drop-target",
      view: (data, { writer }) => {
        if (editor.model.schema.checkChild(data.markerRange.start, "$text")) {
          this._dropTargetLineView.isVisible = false;
          return this._createDropTargetPosition(writer);
        } else {
          if (data.markerRange.isCollapsed) {
            this._updateDropTargetLine(data.markerRange);
          } else {
            this._dropTargetLineView.isVisible = false;
          }
        }
      }
    });
  }
  /**
   * Updates the drop target marker to the provided range.
   *
   * @param targetRange The range to set the marker to.
   */
  _updateDropMarker(targetRange) {
    const editor = this.editor;
    const markers = editor.model.markers;
    editor.model.change((writer) => {
      if (markers.has("drop-target")) {
        if (!markers.get("drop-target").getRange().isEqual(targetRange)) {
          writer.updateMarker("drop-target", { range: targetRange });
        }
      } else {
        writer.addMarker("drop-target", {
          range: targetRange,
          usingOperation: false,
          affectsData: false
        });
      }
    });
  }
  /**
   * Creates the UI element for vertical (in-line) drop target.
   */
  _createDropTargetPosition(writer) {
    return writer.createUIElement("span", { class: "ck ck-clipboard-drop-target-position" }, function(domDocument) {
      const domElement = this.toDomElement(domDocument);
      domElement.append("⁠", domDocument.createElement("span"), "⁠");
      return domElement;
    });
  }
  /**
   * Updates the horizontal drop target line.
   */
  _updateDropTargetLine(range) {
    const editing = this.editor.editing;
    const nodeBefore = range.start.nodeBefore;
    const nodeAfter = range.start.nodeAfter;
    const nodeParent = range.start.parent;
    const viewElementBefore = nodeBefore ? editing.mapper.toViewElement(nodeBefore) : null;
    const domElementBefore = viewElementBefore ? editing.view.domConverter.mapViewToDom(viewElementBefore) : null;
    const viewElementAfter = nodeAfter ? editing.mapper.toViewElement(nodeAfter) : null;
    const domElementAfter = viewElementAfter ? editing.view.domConverter.mapViewToDom(viewElementAfter) : null;
    const viewElementParent = editing.mapper.toViewElement(nodeParent);
    if (!viewElementParent) {
      return;
    }
    const domElementParent = editing.view.domConverter.mapViewToDom(viewElementParent);
    const domScrollableRect = this._getScrollableRect(viewElementParent);
    const { scrollX, scrollY } = global_default.window;
    const rectBefore = domElementBefore ? new Rect(domElementBefore) : null;
    const rectAfter = domElementAfter ? new Rect(domElementAfter) : null;
    const rectParent = new Rect(domElementParent).excludeScrollbarsAndBorders();
    const above = rectBefore ? rectBefore.bottom : rectParent.top;
    const below = rectAfter ? rectAfter.top : rectParent.bottom;
    const parentStyle = global_default.window.getComputedStyle(domElementParent);
    const top = above <= below ? (above + below) / 2 : below;
    if (domScrollableRect.top < top && top < domScrollableRect.bottom) {
      const left = rectParent.left + parseFloat(parentStyle.paddingLeft);
      const right = rectParent.right - parseFloat(parentStyle.paddingRight);
      const leftClamped = Math.max(left + scrollX, domScrollableRect.left);
      const rightClamped = Math.min(right + scrollX, domScrollableRect.right);
      this._dropTargetLineView.set({
        isVisible: true,
        left: leftClamped,
        top: top + scrollY,
        width: rightClamped - leftClamped
      });
    } else {
      this._dropTargetLineView.isVisible = false;
    }
  }
  /**
   * Finds the closest scrollable element rect for the given view element.
   */
  _getScrollableRect(viewElement) {
    const rootName = viewElement.root.rootName;
    let domScrollable;
    if (this._scrollables.has(rootName)) {
      domScrollable = this._scrollables.get(rootName).domElement;
    } else {
      const domElement = this.editor.editing.view.domConverter.mapViewToDom(viewElement);
      domScrollable = findScrollableElement(domElement);
      this._domEmitter.listenTo(domScrollable, "scroll", this._reconvertMarkerThrottled, { usePassive: true });
      const resizeObserver = new resizeobserver_default(domScrollable, this._reconvertMarkerThrottled);
      this._scrollables.set(rootName, {
        domElement: domScrollable,
        resizeObserver
      });
    }
    return new Rect(domScrollable).excludeScrollbarsAndBorders();
  }
};
function findDropTargetRange(editor, targetViewElement, targetViewRanges, clientX, clientY, blockMode, draggedRange) {
  const model = editor.model;
  const mapper = editor.editing.mapper;
  const targetModelElement = getClosestMappedModelElement(editor, targetViewElement);
  let modelElement = targetModelElement;
  while (modelElement) {
    if (!blockMode) {
      if (model.schema.checkChild(modelElement, "$text")) {
        if (targetViewRanges) {
          const targetViewPosition = targetViewRanges[0].start;
          const targetModelPosition = mapper.toModelPosition(targetViewPosition);
          const canDropOnPosition = !draggedRange || Array.from(draggedRange.getItems()).every((item) => model.schema.checkChild(targetModelPosition, item));
          if (canDropOnPosition) {
            if (model.schema.checkChild(targetModelPosition, "$text")) {
              return model.createRange(targetModelPosition);
            } else if (targetViewPosition) {
              return findDropTargetRangeForElement(editor, getClosestMappedModelElement(editor, targetViewPosition.parent), clientX, clientY);
            }
          }
        }
      } else if (model.schema.isInline(modelElement)) {
        return findDropTargetRangeForElement(editor, modelElement, clientX, clientY);
      }
    }
    if (model.schema.isBlock(modelElement)) {
      return findDropTargetRangeForElement(editor, modelElement, clientX, clientY);
    } else if (model.schema.checkChild(modelElement, "$block")) {
      const childNodes = Array.from(modelElement.getChildren()).filter((node) => node.is("element") && !shouldIgnoreElement(editor, node));
      let startIndex = 0;
      let endIndex = childNodes.length;
      if (endIndex == 0) {
        return model.createRange(model.createPositionAt(modelElement, "end"));
      }
      while (startIndex < endIndex - 1) {
        const middleIndex = Math.floor((startIndex + endIndex) / 2);
        const side = findElementSide(editor, childNodes[middleIndex], clientX, clientY);
        if (side == "before") {
          endIndex = middleIndex;
        } else {
          startIndex = middleIndex;
        }
      }
      return findDropTargetRangeForElement(editor, childNodes[startIndex], clientX, clientY);
    }
    modelElement = modelElement.parent;
  }
  return null;
}
function shouldIgnoreElement(editor, modelElement) {
  const mapper = editor.editing.mapper;
  const domConverter = editor.editing.view.domConverter;
  const viewElement = mapper.toViewElement(modelElement);
  if (!viewElement) {
    return true;
  }
  const domElement = domConverter.mapViewToDom(viewElement);
  return global_default.window.getComputedStyle(domElement).float != "none";
}
function findDropTargetRangeForElement(editor, modelElement, clientX, clientY) {
  const model = editor.model;
  return model.createRange(model.createPositionAt(modelElement, findElementSide(editor, modelElement, clientX, clientY)));
}
function findElementSide(editor, modelElement, clientX, clientY) {
  const mapper = editor.editing.mapper;
  const domConverter = editor.editing.view.domConverter;
  const viewElement = mapper.toViewElement(modelElement);
  const domElement = domConverter.mapViewToDom(viewElement);
  const rect = new Rect(domElement);
  if (editor.model.schema.isInline(modelElement)) {
    return clientX < (rect.left + rect.right) / 2 ? "before" : "after";
  } else {
    return clientY < (rect.top + rect.bottom) / 2 ? "before" : "after";
  }
}
function getClosestMappedModelElement(editor, element) {
  const mapper = editor.editing.mapper;
  const view = editor.editing.view;
  const targetModelElement = mapper.toModelElement(element);
  if (targetModelElement) {
    return targetModelElement;
  }
  const viewPosition = view.createPositionBefore(element);
  const viewElement = mapper.findMappedViewAncestor(viewPosition);
  return mapper.toModelElement(viewElement);
}
function findScrollableElement(domNode) {
  let domElement = domNode;
  do {
    domElement = domElement.parentElement;
    const overflow = global_default.window.getComputedStyle(domElement).overflowY;
    if (overflow == "auto" || overflow == "scroll") {
      break;
    }
  } while (domElement.tagName != "BODY");
  return domElement;
}

// node_modules/@ckeditor/ckeditor5-clipboard/src/dragdropblocktoolbar.js
var DragDropBlockToolbar = class extends Plugin {
  constructor() {
    super(...arguments);
    this._isBlockDragging = false;
    this._domEmitter = new (DomEmitterMixin())();
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "DragDropBlockToolbar";
  }
  /**
   * @inheritDoc
   */
  init() {
    const editor = this.editor;
    this.listenTo(editor, "change:isReadOnly", (evt, name, isReadOnly) => {
      if (isReadOnly) {
        this.forceDisabled("readOnlyMode");
        this._isBlockDragging = false;
      } else {
        this.clearForceDisabled("readOnlyMode");
      }
    });
    if (env_default.isAndroid) {
      this.forceDisabled("noAndroidSupport");
    }
    if (editor.plugins.has("BlockToolbar")) {
      const blockToolbar = editor.plugins.get("BlockToolbar");
      const element = blockToolbar.buttonView.element;
      this._domEmitter.listenTo(element, "dragstart", (evt, data) => this._handleBlockDragStart(data));
      this._domEmitter.listenTo(global_default.document, "dragover", (evt, data) => this._handleBlockDragging(data));
      this._domEmitter.listenTo(global_default.document, "drop", (evt, data) => this._handleBlockDragging(data));
      this._domEmitter.listenTo(global_default.document, "dragend", () => this._handleBlockDragEnd(), { useCapture: true });
      if (this.isEnabled) {
        element.setAttribute("draggable", "true");
      }
      this.on("change:isEnabled", (evt, name, isEnabled) => {
        element.setAttribute("draggable", isEnabled ? "true" : "false");
      });
    }
  }
  /**
   * @inheritDoc
   */
  destroy() {
    this._domEmitter.stopListening();
    return super.destroy();
  }
  /**
   * The `dragstart` event handler.
   */
  _handleBlockDragStart(domEvent) {
    if (!this.isEnabled) {
      return;
    }
    const model = this.editor.model;
    const selection = model.document.selection;
    const view = this.editor.editing.view;
    const blocks = Array.from(selection.getSelectedBlocks());
    const draggedRange = model.createRange(model.createPositionBefore(blocks[0]), model.createPositionAfter(blocks[blocks.length - 1]));
    model.change((writer) => writer.setSelection(draggedRange));
    this._isBlockDragging = true;
    view.focus();
    view.getObserver(ClipboardObserver).onDomEvent(domEvent);
  }
  /**
   * The `dragover` and `drop` event handler.
   */
  _handleBlockDragging(domEvent) {
    if (!this.isEnabled || !this._isBlockDragging) {
      return;
    }
    const clientX = domEvent.clientX + (this.editor.locale.contentLanguageDirection == "ltr" ? 100 : -100);
    const clientY = domEvent.clientY;
    const target = document.elementFromPoint(clientX, clientY);
    const view = this.editor.editing.view;
    if (!target || !target.closest(".ck-editor__editable")) {
      return;
    }
    view.getObserver(ClipboardObserver).onDomEvent({
      ...domEvent,
      type: domEvent.type,
      dataTransfer: domEvent.dataTransfer,
      target,
      clientX,
      clientY,
      preventDefault: () => domEvent.preventDefault(),
      stopPropagation: () => domEvent.stopPropagation()
    });
  }
  /**
   * The `dragend` event handler.
   */
  _handleBlockDragEnd() {
    this._isBlockDragging = false;
  }
};

// node_modules/@ckeditor/ckeditor5-clipboard/src/dragdrop.js
import "/tmp/build_5d4c22f1/node_modules/@ckeditor/ckeditor5-clipboard/theme/clipboard.css";
var DragDrop = class extends Plugin {
  constructor() {
    super(...arguments);
    this._clearDraggableAttributesDelayed = delay(() => this._clearDraggableAttributes(), 40);
    this._blockMode = false;
    this._domEmitter = new (DomEmitterMixin())();
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "DragDrop";
  }
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ClipboardPipeline, Widget, DragDropTarget, DragDropBlockToolbar];
  }
  /**
   * @inheritDoc
   */
  init() {
    const editor = this.editor;
    const view = editor.editing.view;
    this._draggedRange = null;
    this._draggingUid = "";
    this._draggableElement = null;
    view.addObserver(ClipboardObserver);
    view.addObserver(MouseObserver);
    this._setupDragging();
    this._setupContentInsertionIntegration();
    this._setupClipboardInputIntegration();
    this._setupDraggableAttributeHandling();
    this.listenTo(editor, "change:isReadOnly", (evt, name, isReadOnly) => {
      if (isReadOnly) {
        this.forceDisabled("readOnlyMode");
      } else {
        this.clearForceDisabled("readOnlyMode");
      }
    });
    this.on("change:isEnabled", (evt, name, isEnabled) => {
      if (!isEnabled) {
        this._finalizeDragging(false);
      }
    });
    if (env_default.isAndroid) {
      this.forceDisabled("noAndroidSupport");
    }
  }
  /**
   * @inheritDoc
   */
  destroy() {
    if (this._draggedRange) {
      this._draggedRange.detach();
      this._draggedRange = null;
    }
    if (this._previewContainer) {
      this._previewContainer.remove();
    }
    this._domEmitter.stopListening();
    this._clearDraggableAttributesDelayed.cancel();
    return super.destroy();
  }
  /**
   * Drag and drop events handling.
   */
  _setupDragging() {
    const editor = this.editor;
    const model = editor.model;
    const view = editor.editing.view;
    const viewDocument = view.document;
    const dragDropTarget = editor.plugins.get(DragDropTarget);
    this.listenTo(viewDocument, "dragstart", (evt, data) => {
      if (data.target && data.target.is("editableElement")) {
        data.preventDefault();
        return;
      }
      this._prepareDraggedRange(data.target);
      if (!this._draggedRange) {
        data.preventDefault();
        return;
      }
      this._draggingUid = uid();
      data.dataTransfer.effectAllowed = this.isEnabled ? "copyMove" : "copy";
      data.dataTransfer.setData("application/ckeditor5-dragging-uid", this._draggingUid);
      const draggedSelection = model.createSelection(this._draggedRange.toRange());
      const clipboardPipeline = this.editor.plugins.get("ClipboardPipeline");
      clipboardPipeline._fireOutputTransformationEvent(data.dataTransfer, draggedSelection, "dragstart");
      const { dataTransfer, domTarget, domEvent } = data;
      const { clientX } = domEvent;
      this._updatePreview({ dataTransfer, domTarget, clientX });
      data.stopPropagation();
      if (!this.isEnabled) {
        this._draggedRange.detach();
        this._draggedRange = null;
        this._draggingUid = "";
      }
    }, { priority: "low" });
    this.listenTo(viewDocument, "dragend", (evt, data) => {
      this._finalizeDragging(!data.dataTransfer.isCanceled && data.dataTransfer.dropEffect == "move");
    }, { priority: "low" });
    this._domEmitter.listenTo(global_default.document, "dragend", () => {
      this._blockMode = false;
    }, { useCapture: true });
    this.listenTo(viewDocument, "dragenter", () => {
      if (!this.isEnabled) {
        return;
      }
      view.focus();
    });
    this.listenTo(viewDocument, "dragleave", () => {
      dragDropTarget.removeDropMarkerDelayed();
    });
    this.listenTo(viewDocument, "dragging", (evt, data) => {
      if (!this.isEnabled) {
        data.dataTransfer.dropEffect = "none";
        return;
      }
      const { clientX, clientY } = data.domEvent;
      dragDropTarget.updateDropMarker(data.target, data.targetRanges, clientX, clientY, this._blockMode, this._draggedRange);
      if (!this._draggedRange) {
        data.dataTransfer.dropEffect = "copy";
      }
      if (!env_default.isGecko) {
        if (data.dataTransfer.effectAllowed == "copy") {
          data.dataTransfer.dropEffect = "copy";
        } else if (["all", "copyMove"].includes(data.dataTransfer.effectAllowed)) {
          data.dataTransfer.dropEffect = "move";
        }
      }
      evt.stop();
    }, { priority: "low" });
  }
  /**
   * Integration with the `clipboardInput` event.
   */
  _setupClipboardInputIntegration() {
    const editor = this.editor;
    const view = editor.editing.view;
    const viewDocument = view.document;
    const dragDropTarget = editor.plugins.get(DragDropTarget);
    this.listenTo(viewDocument, "clipboardInput", (evt, data) => {
      if (data.method != "drop") {
        return;
      }
      const { clientX, clientY } = data.domEvent;
      const targetRange = dragDropTarget.getFinalDropRange(data.target, data.targetRanges, clientX, clientY, this._blockMode, this._draggedRange);
      if (!targetRange) {
        this._finalizeDragging(false);
        evt.stop();
        return;
      }
      if (this._draggedRange && this._draggingUid != data.dataTransfer.getData("application/ckeditor5-dragging-uid")) {
        this._draggedRange.detach();
        this._draggedRange = null;
        this._draggingUid = "";
      }
      const isMove = getFinalDropEffect(data.dataTransfer) == "move";
      if (isMove && this._draggedRange && this._draggedRange.containsRange(targetRange, true)) {
        this._finalizeDragging(false);
        evt.stop();
        return;
      }
      data.targetRanges = [editor.editing.mapper.toViewRange(targetRange)];
    }, { priority: "high" });
  }
  /**
   * Integration with the `contentInsertion` event of the clipboard pipeline.
   */
  _setupContentInsertionIntegration() {
    const clipboardPipeline = this.editor.plugins.get(ClipboardPipeline);
    clipboardPipeline.on("contentInsertion", (evt, data) => {
      if (!this.isEnabled || data.method !== "drop") {
        return;
      }
      const ranges = data.targetRanges.map((viewRange) => this.editor.editing.mapper.toModelRange(viewRange));
      this.editor.model.change((writer) => writer.setSelection(ranges));
    }, { priority: "high" });
    clipboardPipeline.on("contentInsertion", (evt, data) => {
      if (!this.isEnabled || data.method !== "drop") {
        return;
      }
      const isMove = getFinalDropEffect(data.dataTransfer) == "move";
      const isSuccess = !data.resultRange || !data.resultRange.isCollapsed;
      this._finalizeDragging(isSuccess && isMove);
    }, { priority: "lowest" });
  }
  /**
   * Adds listeners that add the `draggable` attribute to the elements while the mouse button is down so the dragging could start.
   */
  _setupDraggableAttributeHandling() {
    const editor = this.editor;
    const view = editor.editing.view;
    const viewDocument = view.document;
    this.listenTo(viewDocument, "mousedown", (evt, data) => {
      if (env_default.isAndroid || !data) {
        return;
      }
      this._clearDraggableAttributesDelayed.cancel();
      let draggableElement = findDraggableWidget(data.target);
      if (env_default.isBlink && !editor.isReadOnly && !draggableElement && !viewDocument.selection.isCollapsed) {
        const selectedElement = viewDocument.selection.getSelectedElement();
        if (!selectedElement || !isWidget(selectedElement)) {
          draggableElement = viewDocument.selection.editableElement;
        }
      }
      if (draggableElement) {
        view.change((writer) => {
          writer.setAttribute("draggable", "true", draggableElement);
        });
        this._draggableElement = editor.editing.mapper.toModelElement(draggableElement);
      }
    });
    this.listenTo(viewDocument, "mouseup", () => {
      if (!env_default.isAndroid) {
        this._clearDraggableAttributesDelayed();
      }
    });
  }
  /**
   * Removes the `draggable` attribute from the element that was used for dragging.
   */
  _clearDraggableAttributes() {
    const editing = this.editor.editing;
    editing.view.change((writer) => {
      if (this._draggableElement && this._draggableElement.root.rootName != "$graveyard") {
        writer.removeAttribute("draggable", editing.mapper.toViewElement(this._draggableElement));
      }
      this._draggableElement = null;
    });
  }
  /**
   * Deletes the dragged content from its original range and clears the dragging state.
   *
   * @param moved Whether the move succeeded.
   */
  _finalizeDragging(moved) {
    const editor = this.editor;
    const model = editor.model;
    const dragDropTarget = editor.plugins.get(DragDropTarget);
    dragDropTarget.removeDropMarker();
    this._clearDraggableAttributes();
    if (editor.plugins.has("WidgetToolbarRepository")) {
      const widgetToolbarRepository = editor.plugins.get("WidgetToolbarRepository");
      widgetToolbarRepository.clearForceDisabled("dragDrop");
    }
    this._draggingUid = "";
    if (this._previewContainer) {
      this._previewContainer.remove();
      this._previewContainer = void 0;
    }
    if (!this._draggedRange) {
      return;
    }
    if (moved && this.isEnabled) {
      model.change((writer) => {
        const selection = model.createSelection(this._draggedRange);
        model.deleteContent(selection, { doNotAutoparagraph: true });
        const selectionParent = selection.getFirstPosition().parent;
        if (selectionParent.isEmpty && !model.schema.checkChild(selectionParent, "$text") && model.schema.checkChild(selectionParent, "paragraph")) {
          writer.insertElement("paragraph", selectionParent, 0);
        }
      });
    }
    this._draggedRange.detach();
    this._draggedRange = null;
  }
  /**
   * Sets the dragged source range based on event target and document selection.
   */
  _prepareDraggedRange(target) {
    const editor = this.editor;
    const model = editor.model;
    const selection = model.document.selection;
    const draggableWidget = target ? findDraggableWidget(target) : null;
    if (draggableWidget) {
      const modelElement = editor.editing.mapper.toModelElement(draggableWidget);
      this._draggedRange = LiveRange.fromRange(model.createRangeOn(modelElement));
      this._blockMode = model.schema.isBlock(modelElement);
      if (editor.plugins.has("WidgetToolbarRepository")) {
        const widgetToolbarRepository = editor.plugins.get("WidgetToolbarRepository");
        widgetToolbarRepository.forceDisabled("dragDrop");
      }
      return;
    }
    if (selection.isCollapsed && !selection.getFirstPosition().parent.isEmpty) {
      return;
    }
    const blocks = Array.from(selection.getSelectedBlocks());
    const draggedRange = selection.getFirstRange();
    if (blocks.length == 0) {
      this._draggedRange = LiveRange.fromRange(draggedRange);
      return;
    }
    const blockRange = getRangeIncludingFullySelectedParents(model, blocks);
    if (blocks.length > 1) {
      this._draggedRange = LiveRange.fromRange(blockRange);
      this._blockMode = true;
    } else if (blocks.length == 1) {
      const touchesBlockEdges = draggedRange.start.isTouching(blockRange.start) && draggedRange.end.isTouching(blockRange.end);
      this._draggedRange = LiveRange.fromRange(touchesBlockEdges ? blockRange : draggedRange);
      this._blockMode = touchesBlockEdges;
    }
    model.change((writer) => writer.setSelection(this._draggedRange.toRange()));
  }
  /**
   * Updates the dragged preview image.
   */
  _updatePreview({ dataTransfer, domTarget, clientX }) {
    const view = this.editor.editing.view;
    const editable = view.document.selection.editableElement;
    const domEditable = view.domConverter.mapViewToDom(editable);
    const computedStyle = global_default.window.getComputedStyle(domEditable);
    if (!this._previewContainer) {
      this._previewContainer = createElement(global_default.document, "div", {
        style: "position: fixed; left: -999999px;"
      });
      global_default.document.body.appendChild(this._previewContainer);
    } else if (this._previewContainer.firstElementChild) {
      this._previewContainer.removeChild(this._previewContainer.firstElementChild);
    }
    const domRect = new Rect(domEditable);
    if (domEditable.contains(domTarget)) {
      return;
    }
    const domEditablePaddingLeft = parseFloat(computedStyle.paddingLeft);
    const preview = createElement(global_default.document, "div");
    preview.className = "ck ck-content";
    preview.style.width = computedStyle.width;
    preview.style.paddingLeft = `${domRect.left - clientX + domEditablePaddingLeft}px`;
    if (env_default.isiOS) {
      preview.style.backgroundColor = "white";
    }
    preview.innerHTML = dataTransfer.getData("text/html");
    dataTransfer.setDragImage(preview, 0, 0);
    this._previewContainer.appendChild(preview);
  }
};
function getFinalDropEffect(dataTransfer) {
  if (env_default.isGecko) {
    return dataTransfer.dropEffect;
  }
  return ["all", "copyMove"].includes(dataTransfer.effectAllowed) ? "move" : "copy";
}
function findDraggableWidget(target) {
  if (target.is("editableElement")) {
    return null;
  }
  if (target.hasClass("ck-widget__selection-handle")) {
    return target.findAncestor(isWidget);
  }
  if (isWidget(target)) {
    return target;
  }
  const ancestor = target.findAncestor((node) => isWidget(node) || node.is("editableElement"));
  if (isWidget(ancestor)) {
    return ancestor;
  }
  return null;
}
function getRangeIncludingFullySelectedParents(model, elements) {
  const firstElement = elements[0];
  const lastElement = elements[elements.length - 1];
  const parent = firstElement.getCommonAncestor(lastElement);
  const startPosition = model.createPositionBefore(firstElement);
  const endPosition = model.createPositionAfter(lastElement);
  if (parent && parent.is("element") && !model.schema.isLimit(parent)) {
    const parentRange = model.createRangeOn(parent);
    const touchesStart = startPosition.isTouching(parentRange.start);
    const touchesEnd = endPosition.isTouching(parentRange.end);
    if (touchesStart && touchesEnd) {
      return getRangeIncludingFullySelectedParents(model, [parent]);
    }
  }
  return model.createRange(startPosition, endPosition);
}

// node_modules/@ckeditor/ckeditor5-clipboard/src/pasteplaintext.js
var PastePlainText = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "PastePlainText";
  }
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ClipboardPipeline];
  }
  /**
   * @inheritDoc
   */
  init() {
    const editor = this.editor;
    const model = editor.model;
    const view = editor.editing.view;
    const viewDocument = view.document;
    const selection = model.document.selection;
    let shiftPressed = false;
    view.addObserver(ClipboardObserver);
    this.listenTo(viewDocument, "keydown", (evt, data) => {
      shiftPressed = data.shiftKey;
    });
    editor.plugins.get(ClipboardPipeline).on("contentInsertion", (evt, data) => {
      if (!shiftPressed && !isPlainTextFragment(data.content, model.schema)) {
        return;
      }
      model.change((writer) => {
        const textAttributes = Array.from(selection.getAttributes()).filter(([key]) => model.schema.getAttributeProperties(key).isFormatting);
        if (!selection.isCollapsed) {
          model.deleteContent(selection, { doNotAutoparagraph: true });
        }
        textAttributes.push(...selection.getAttributes());
        const range = writer.createRangeIn(data.content);
        for (const item of range.getItems()) {
          if (item.is("$textProxy")) {
            writer.setAttributes(textAttributes, item);
          }
        }
      });
    });
  }
};
function isPlainTextFragment(documentFragment, schema) {
  if (documentFragment.childCount > 1) {
    return false;
  }
  const child = documentFragment.getChild(0);
  if (schema.isObject(child)) {
    return false;
  }
  return Array.from(child.getAttributeKeys()).length == 0;
}

// node_modules/@ckeditor/ckeditor5-clipboard/src/clipboard.js
var Clipboard = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "Clipboard";
  }
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ClipboardPipeline, DragDrop, PastePlainText];
  }
};

export {
  ClipboardPipeline,
  Clipboard
};
/*! Bundled license information:

@ckeditor/ckeditor5-clipboard/src/clipboardobserver.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-clipboard/src/utils/plaintexttohtml.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-clipboard/src/utils/normalizeclipboarddata.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-clipboard/src/utils/viewtoplaintext.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-clipboard/src/clipboardpipeline.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-clipboard/src/lineview.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)
  (* istanbul ignore file -- @preserve *)

@ckeditor/ckeditor5-clipboard/src/dragdroptarget.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)
  (* istanbul ignore next -- @preserve *)

@ckeditor/ckeditor5-clipboard/src/dragdropblocktoolbar.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-clipboard/src/dragdrop.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-clipboard/src/pasteplaintext.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-clipboard/src/clipboard.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-clipboard/src/augmentation.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-clipboard/src/index.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

ckeditor5/src/clipboard.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)
*/
//# sourceMappingURL=chunk-DJCMRWX5.js.map
